import {
  FC, useEffect, useMemo,
} from 'react';
import {
  Control, Controller, UseFormSetValue, UseFormWatch,
} from 'react-hook-form';
import { useTranslation } from 'react-i18next';

import { Mixpanel } from 'mixpanel';
import useUser from 'api/hooks/useUser';
import Button from 'components/Button';
import Input from 'components/Input';
import Select from 'components/Select';
import { BriefProps } from 'utils/types';
import { IFeasibilityCheck } from 'features/FeasibilityCheck';

interface Props {
  briefData: BriefProps[];
  setActiveTab: (value: number) => void;
  setValue: UseFormSetValue<IFeasibilityCheck>;
  formWatch: UseFormWatch<IFeasibilityCheck>;
  control: Control<IFeasibilityCheck>;
}

const ApplicationFeasibility: FC<Props> = ({
  briefData,
  setActiveTab,
  setValue,
  formWatch,
  control,
}) => {
  const { t } = useTranslation();

  const {
    industry,
    subIndustry,
    apps,
    product,
  } = formWatch('application');

  const { data: user } = useUser();

  const handleNext = () => {
    if (user) {
      Mixpanel.track('Feasibility Check Product', {
        role: user?.role?.name,
        organization: user?.organization,
        product: product?.value || '',
      });
    }

    setActiveTab(2);
    Mixpanel.track('Feasibility Check Step 1', {
      role: user?.role?.name,
      organization: user?.organization,
    });
  };

  const industrySelect = useMemo(() => {
    const result: string[] = [];
    briefData?.forEach((brief) => {
      if (brief?.Industry) {
        result.push(brief?.Industry);
      }
    });
    const unique = [...new Set(result)];

    unique.push(t('other'));

    return unique.map((uniqBrief) => ({ value: uniqBrief,
      label: uniqBrief }));
  }, [briefData]);

  const getSubIndustryList = useMemo(() => {
    const result: string[] = [];

    if (industry?.value) {
      briefData?.forEach((briefItem: BriefProps) => {
        if (briefItem?.Industry === industry?.value && briefItem['Sub-Industry']) {
          result.push(briefItem['Sub-Industry']);
        }
      });
    }

    const unique = [...new Set(result)];

    if (unique.length < 1 && industry?.value !== t('other')) {
      unique.push(`${t('noSubSegment')}`);
    }

    unique.push(t('other'));

    return unique.map((subIndustryItem) => ({
      value: subIndustryItem,
      label: subIndustryItem,
    }));
  }, [industry]);

  const getApplicationList = useMemo(() => {
    const result: string[] = [];

    if (industry?.value) {
      briefData?.forEach((briefItem: BriefProps) => {
        if (subIndustry?.value) {
          if (
            briefItem?.Industry === industry?.value &&
            (briefItem?.['Sub-Industry'] === subIndustry?.value || subIndustry?.value === `${t('noSubSegment')}`)
          ) {
            result.push(briefItem?.Application);
          }
        } else {
          if (briefItem?.Industry === industry?.value) {
            result.push(briefItem?.Application);
          }
        }
      });
    }

    const unique = [...new Set(result)];
    unique.push(t('other'));

    return unique.map((application) => ({
      value: application,
      label: application,
    }));
  }, [industry, subIndustry]);

  const getProductsList = useMemo(() => {
    const result: string[] = [];

    if (industry?.value) {
      briefData?.forEach((briefItem: BriefProps) => {
        if (subIndustry?.value) {
          if (
            briefItem?.Industry === industry?.value &&
            (briefItem?.['Sub-Industry'] === subIndustry?.value || subIndustry?.value === `${t('noSubSegment')}`) &&
            briefItem?.Application === apps?.value
          ) {
            result.push(briefItem['Product recommendation']);
          }
        } else {
          if (
            briefItem?.Industry === industry?.value &&
            briefItem?.Application === apps?.value
          ) {
            result.push(briefItem['Product recommendation']);
          }
        }
      });
    }

    const unique = [...new Set(result)];

    unique.push(t('other'));

    return unique.map((productItem) => ({
      value: productItem,
      label: productItem,
    }));
  }, [industry, subIndustry, apps]);

  useEffect(() => {
    if (
      getSubIndustryList?.length === 1 &&
      getSubIndustryList?.[0]?.value !== null &&
      getSubIndustryList?.[0].value !== t('other')
    ) {
      setValue('application.subIndustry', getSubIndustryList[0]);
    }
  }, [industry]);

  useEffect(() => {
    if (
      getApplicationList?.length === 1 &&
      getApplicationList?.[0]?.value !== null &&
      getApplicationList?.[0].value !== t('other')
    ) {
      setValue('application.apps', getApplicationList[0]);
    }
  }, [subIndustry, industry]);

  useEffect(() => {
    if (
      getProductsList?.length &&
      getProductsList?.[0]?.value !== null &&
      getProductsList?.[0].value !== t('other')
    ) {
      setValue('application.product', getProductsList[0]);
    }
  }, [apps]);

  useEffect(() => {
    setValue('application.productsOptions',
      getProductsList
        .filter((item) => item.value !== t('other'))
        .map(item => item.value));
  }, [getProductsList, setValue, t]);

  const disabledState = useMemo(
    () => !(industry?.value && apps?.value && !!product?.value),
    [industry?.value, apps?.value, product?.value],
  );

  return (
    <form className="border border-primitiveGrey p-8 md:py-6 md:px-4 rounded-2xl">
      <div className="text-black text-xl font-bold mb-8">
        {t('applicationSelection')}
      </div>
      <div className="grid gap-6 grid-cols-2 grid-rows-2 md:grid-cols-1 md:gap-2">
        <div className="w-full mb-1 md:mb-4">
          <Controller
            control={control}
            rules={{
              required: true,
            }}
            render={({ field: { onChange } }) => (
              <Input
                titleClassName="top-[-9px] left-[8px]"
                className="w-full"
                title={t('companyName')}
                placeholder={t('enterCompanyName')}
                onChange={onChange}
                type="text"
                maxLength={40}
              />
            )}
            name="application.companyName"
          />
        </div>
        <div className="w-full mb-1 md:mb-4 md:hidden"></div>
        <div className="w-full mb-1 md:mb-4">
          <Controller
            control={control}
            rules={{
              required: true,
            }}
            render={({ field: { onChange, value } }) => (
              <Select
                value={value ? [value] : []}
                options={industrySelect}
                placeholder={t('specify')}
                title={t('industry')}
                onChange={(values) => {
                  onChange(values);
                  setValue('application.subIndustry', null);
                  setValue('application.apps', null);
                  setValue('application.product', null);
                }}
              />
            )}
            name="application.industry"
          />
        </div>
        <div className="w-full mb-1 md:mb-4">
          <Controller
            control={control}
            rules={{
              required: true,
            }}
            render={({ field: { onChange, value } }) => (
              <Select
                disabled={!industry?.value}
                options={getSubIndustryList}
                value={value ? [value] : []}
                placeholder={t('specify')}
                title={t('industrySubSegment')}
                onChange={(values) => {
                  onChange(values);
                  setValue('application.apps', null);
                  setValue('application.product', null);
                }}
              />
            )}
            name="application.subIndustry"
          />
        </div>
        <div className="w-full mb-1 md:mb-4">
          <Controller
            control={control}
            rules={{
              required: true,
            }}
            render={({ field: { onChange, value } }) => (
              <Select
                disabled={
                  !industry?.value ||
              (getSubIndustryList?.[0]?.value !== null
                ? !subIndustry?.value
                : false)
                }
                value={value ? [value] : []}
                options={getApplicationList}
                placeholder={t('specify')}
                title={t('applications')}
                onChange={(values) => {
                  onChange(values);
                  setValue('application.product', null);
                }}
              />
            )}
            name="application.apps"
          />
        </div>
        <div className="w-full mb-1 md:mb-4">
          <Controller
            control={control}
            rules={{
              required: true,
            }}
            render={({ field: { onChange, value } }) => (
              <Select
                disabled={
                  !industry?.value ||
              (getSubIndustryList?.[0]?.value !== null &&
                !subIndustry?.value) ||
              !apps?.value
                }
                value={value ? [value] : []}
                options={getProductsList}
                placeholder={t('specify')}
                title={t('products')}
                onChange={onChange}
              />
            )}
            name="application.product"
          />
        </div>
      </div>
      <div className="flex justify-center mt-8">
        <div className="flex justify-end md:justify-center">
          <Button onClick={handleNext} disabled={disabledState}>
            {t('next')}
          </Button>
        </div>
      </div>
    </form>
  );
};

export default ApplicationFeasibility;
